import { Link } from 'react-router-dom';
import { siteInfo, siteStats } from '@/mocks/site';
import Breadcrumb from '@/components/base/Breadcrumb';
import SeoMeta from '@/components/base/SeoMeta';
import JsonLd from '@/components/base/JsonLd';

const principles = [
  {
    icon: 'ri-link-m',
    title: '来源可追溯',
    text: '每条结论都尽量指向可点开的原始材料：通告单、完整流水、鉴定摘要或官方通报。没有来源的描述会明确标注为“待核实”。',
  },
  {
    icon: 'ri-user-unfollow-line',
    title: '人物匿名化',
    text: '传闻被证实之前不指向具体个人。我们讨论传播逻辑，不消费真实的人。这是整个站点最重要的一条底线。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '不预设立场',
    text: '不把“反转”当成另一种结论来追捧。反转本身同样需要核实，判断标准始终是材料，而不是情绪上的输赢。',
  },
  {
    icon: 'ri-refresh-line',
    title: '更正有记录',
    text: '内容有误就改，改了就留痕。每一次更正都会标注时间与更正内容，读者可以自己对照前后版本。',
  },
];

const workflow = [
  { step: '线索收集', text: '整理读者投稿与公开可见的热点话题，筛掉没有讨论价值的纯情绪内容。' },
  { step: '材料核对', text: '寻找可核对的原始材料，确认时间、地点与发布主体是否一致。' },
  { step: '状态标注', text: '根据核实进度标记为已反转、已澄清或澄清中，并同步更新事件时间线。' },
  { step: '持续跟进', text: '重大事件在后续出现新材料时重新核查，保证档案里的结论始终是最新的。' },
];

const testimonials = [
  {
    quote: '以前刷到热搜只会跟着情绪走，现在会先来这里看看有没有后续，整个人冷静了不少。',
    name: '读者 · 阿舟',
  },
  {
    quote: '最喜欢时间线那部分，一屏就把前后经过讲清楚了，比翻几十条博省事得多。',
    name: '读者 · 林一笑',
  },
  {
    quote: '匿名处理这点很加分，讨论事件本身而不是扒人，看的时候没有什么负担。',
    name: '读者 · 程可',
  },
];

export default function About() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const basePath = (__BASE_PATH__ || '/').replace(/\/$/, '');

  return (
    <>
      <SeoMeta
        title="关于我们｜真相回旋镖的内容原则与核实流程"
        description="了解真相回旋镖的站点定位、内容原则与核实流程。我们以时间线方式整理爆料反转与辟谣澄清，坚持来源可追溯、人物匿名化、更正有记录。"
        keywords="关于真相回旋镖,内容原则,核实流程,辟谣澄清,爆料反转"
        path="/about"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于我们',
          url: `${origin}${basePath}/about`,
          description: '真相回旋镖的站点定位、内容原则与核实流程说明。',
          mainEntity: {
            '@type': 'Organization',
            name: '真相回旋镖',
            email: siteInfo.email,
            description: siteInfo.intro,
          },
        }}
      />

      <section className="border-b border-background-300 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-16">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <span className="text-xs font-semibold font-label uppercase tracking-widest text-primary-600">
                About Us
              </span>
              <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
                一个不放大情绪的爆料观察站
              </h1>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                {siteInfo.intro}
              </p>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600">
                我们不生产爆料，也不替任何人下结论。这里只做一件相对枯燥但有用的事：把公开可见的材料按时间顺序摆在一起，让你自己看清黑料背后的真实走向。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-300 bg-background-300">
                {siteStats.map((item) => (
                  <div key={item.label} className="bg-background-50 p-4 md:p-5">
                    <p className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      {item.value}
                    </p>
                    <p className="mt-1.5 text-xs font-label text-foreground-600">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="principle-heading" className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
        <div className="reveal mb-9">
          <span className="text-xs font-semibold font-label uppercase tracking-widest text-secondary-600">
            Our Principles
          </span>
          <h2 id="principle-heading" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            内容原则
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {principles.map((item) => (
            <div
              key={item.title}
              className="reveal rounded-lg border border-background-300 bg-background-50 p-5 md:p-6"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                <i className={`${item.icon} text-xl`} aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section aria-labelledby="workflow-heading" className="border-y border-background-300 bg-background-100">
        <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="reveal lg:col-span-4">
              <span className="text-xs font-semibold font-label uppercase tracking-widest text-primary-600">
                Verification Flow
              </span>
              <h2 id="workflow-heading" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                一条案例是怎么被整理的
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                从收到线索到页面更新，大致会经过四步。每一步都尽量留下可复核的痕迹。
              </p>
              <Link
                to="/faq"
                className="mt-6 inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-label text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                了解常见问题
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
            <div className="reveal lg:col-span-8">
              <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {workflow.map((item, index) => (
                  <li
                    key={item.step}
                    className="rounded-lg border border-background-300 bg-background-50 p-5"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-xs font-semibold font-label text-background-50">
                        {index + 1}
                      </span>
                      <h3 className="font-heading text-base font-bold text-foreground-950">{item.step}</h3>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="voice-heading" className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
        <div className="reveal mb-9 text-center">
          <span className="text-xs font-semibold font-label uppercase tracking-widest text-secondary-600">
            Reader Voices
          </span>
          <h2 id="voice-heading" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            读者怎么说
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((item) => (
            <figure
              key={item.name}
              className="reveal flex h-full flex-col justify-between rounded-lg border border-background-300 bg-background-50 p-5 md:p-6"
            >
              <div>
                <i className="ri-double-quotes-l text-2xl text-accent-500" aria-hidden="true" />
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">
                  {item.quote}
                </blockquote>
              </div>
              <figcaption className="mt-5 border-t border-background-200 pt-3 text-xs font-label text-foreground-500">
                {item.name}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section
        id="contact"
        aria-labelledby="contact-heading"
        className="border-t border-background-300 bg-background-100 scroll-mt-24"
      >
        <div className="mx-auto w-full px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="reveal lg:col-span-5">
              <span className="text-xs font-semibold font-label uppercase tracking-widest text-primary-600">
                Contact
              </span>
              <h2 id="contact-heading" className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                联系我们
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                投稿、补充后续进展或反馈内容问题，都可以直接发邮件给我们。附上原始链接或可核对材料，会大大提高处理效率。
              </p>
              <a
                href={`mailto:${siteInfo.email}`}
                rel="nofollow"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-send-line" aria-hidden="true" />
                {siteInfo.email}
              </a>
            </div>
            <div className="reveal lg:col-span-7">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="rounded-lg border border-background-300 bg-background-50 p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-950">投稿与线索</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    请注明事件发生的大致时间与涉及的公开材料，我们会先判断是否具备可核实的条件。
                  </p>
                </div>
                <div className="rounded-lg border border-background-300 bg-background-50 p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-950">内容更正</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    如果案例中的表述与你掌握的材料不符，请一并附上依据。核实后我们会更新页面并标注更正记录。
                  </p>
                </div>
                <div className="rounded-lg border border-background-300 bg-background-50 p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-950">回应时间</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    一般会在三个工作日内给出初步回复。涉及持续跟进的事件，我们会在时间线更新后同步告知。
                  </p>
                </div>
                <div className="rounded-lg border border-background-300 bg-background-50 p-5">
                  <h3 className="font-heading text-base font-bold text-foreground-950">内容声明</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{siteInfo.notice}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}